import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Minus, X } from "lucide-react";
import Stars from "@/components/site/Stars";
import { useCompare } from "@/lib/compare";
import { useLang } from "@/lib/i18n";
import { api, BLOCKS, PRICE_KEY, fmtUSD, imgSrc } from "@/lib/api";
import type { Yacht } from "@/types";

export default function Compare() {
  const { t, lang } = useLang();
  const { ids, remove, clear } = useCompare();
  const { data = [], isLoading } = useQuery<Yacht[]>({ queryKey: ["yachts"], queryFn: async () => (await api.get("/yachts")).data });
  const sel = ids.map((id) => data.find((y) => y.id === id)).filter(Boolean) as Yacht[];
  const amen = Array.from(new Set(sel.flatMap((y) => y.amenities)));
  const best = (vals: number[], lowest: boolean) => (lowest ? Math.min(...vals) : Math.max(...vals));
  const cell = "px-3 py-3 sm:px-4 text-sm text-center border-b border-slate-100";
  const head = "px-3 py-3 sm:px-4 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100 bg-slate-50/60 whitespace-nowrap";

  const row = (label: string, render: (y: Yacht) => React.ReactNode, testId: string) => (
    <tr data-testid={`compare-row-${testId}`}><th scope="row" className={head}>{label}</th>{sel.map((y) => <td key={y.id} className={cell}>{render(y)}</td>)}</tr>
  );
  const numRow = (label: string, get: (y: Yacht) => number, fmt: (n: number) => string, lowest: boolean, testId: string) => {
    const vals = sel.map(get); const b = best(vals, lowest);
    return row(label, (y) => <span className={get(y) === b && sel.length > 1 ? "font-semibold text-[#008C86]" : "text-[#0B192C]"}>{fmt(get(y))}</span>, testId);
  };

  return (
    <div data-testid="compare-page">
      <section className="bg-[#0B192C] relative grain">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-16">
          <Link to="/yachts" data-testid="compare-back" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"><ArrowLeft className="h-4 w-4" />{t("cmp.back")}</Link>
          <p className="mt-6 eyebrow !text-[#D4AF37]">{t("cmp.eyebrow")}</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white">{t("cmp.title")}</h1>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {isLoading ? null : sel.length < 2 ? (
          <div className="text-center py-16" data-testid="compare-empty">
            <p className="font-display text-2xl text-slate-500 max-w-xl mx-auto">{t("cmp.empty")}</p>
            <Link to="/yachts" className="mt-8 inline-flex rounded-full bg-[#0B192C] px-6 py-3 text-sm font-semibold text-white">{t("cmp.pick")}</Link>
          </div>
        ) : (
          <>
            <div className="flex justify-end"><button onClick={clear} data-testid="compare-clear" className="text-sm font-semibold text-[#008C86] hover:underline">{t("cmp.clear")}</button></div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full min-w-[640px] border-collapse" data-testid="compare-table">
                <thead>
                  <tr>
                    <th className={`${head} w-40`} />
                    {sel.map((y) => (
                      <th key={y.id} className="px-3 py-4 sm:px-4 border-b border-slate-100 align-top" data-testid={`compare-col-${y.id}`}>
                        <div className="relative">
                          <button onClick={() => remove(y.id)} aria-label="x" data-testid={`compare-remove-${y.id}`} className="absolute -top-1 -right-1 z-10 grid h-6 w-6 place-items-center rounded-full bg-white border border-slate-200 text-slate-500 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                          <img src={imgSrc(y.photos[0])} alt={y.name} className="h-28 w-full rounded-lg object-cover" />
                        </div>
                        <p className="mt-3 text-[10px] uppercase tracking-wider text-slate-500">{t(`type.${y.type}`)}</p>
                        <Link to={`/yachts/${y.id}`} className="font-display text-xl text-[#0B192C] hover:text-[#008C86]">{y.name}</Link>
                        {y.rating && <div className="mt-1 flex justify-center items-center gap-1"><Stars value={y.rating.avg} size="h-3 w-3" testId={`compare-stars-${y.id}`} /><span className="text-xs text-slate-500">{y.rating.avg.toFixed(1)}</span></div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {numRow(t("card.ft"), (y) => y.length_ft, (n) => `${n} ${t("card.ft")}`, false, "length")}
                  {numRow(t("cmp.capacity"), (y) => y.capacity, (n) => `${n} ${t("card.guests")}`, false, "capacity")}
                  {numRow(t("card.cabins"), (y) => y.cabins, (n) => String(n), false, "cabins")}
                  {row(t("cmp.port"), (y) => <span className="text-slate-600">{y.port}</span>, "port")}
                  {BLOCKS.map((b) => numRow(t(`block.${b}`), (y) => y.prices[PRICE_KEY[b]] || 0, (n) => (n ? fmtUSD(n) : "—"), true, `price-${b}`))}
                  {numRow(t("cmp.perGuest"), (y) => Math.round(y.prices.h4 / y.capacity), (n) => `${fmtUSD(n)} / ${t("cmp.guest")}`, true, "per-guest")}
                  {amen.map((a) => row(t(`am.${a}`), (y) => y.amenities.includes(a)
                    ? <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-[#00D2C8]/15"><Check className="h-3.5 w-3.5 text-[#008C86]" /></span>
                    : <Minus className="inline h-4 w-4 text-slate-300" />, `am-${a}`))}
                  <tr><td className={head} />{sel.map((y) => <td key={y.id} className="px-3 py-4 text-center"><Link to={`/yachts/${y.id}`} data-testid={`compare-book-${y.id}`} className="inline-flex rounded-full bg-[#D4AF37] px-5 py-2.5 text-xs font-semibold text-[#0B192C] hover:bg-[#F3E5AB]">{t("cmp.book")}</Link></td>)}</tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-slate-500">{lang === "es" ? "En verde: el mejor valor de cada fila." : "In green: the best value in each row."}</p>
          </>
        )}
      </section>
    </div>
  );
}
